import { useState, useEffect, createContext, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Cookie, Shield, BarChart3, Megaphone, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Contexte pour le consentement cookies
const CookieConsentContext = createContext();

export function useCookieConsent() {
  return useContext(CookieConsentContext);
}

// Clés localStorage
const CONSENT_KEY = 'mutagim_cookie_consent';
const CONSENT_TIMESTAMP_KEY = 'mutagim_cookie_consent_timestamp';

export function CookieConsentProvider({ children }) {
  const [consent, setConsent] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    // Charger le consentement depuis localStorage
    const savedConsent = localStorage.getItem(CONSENT_KEY);
    if (savedConsent) {
      try {
        setConsent(JSON.parse(savedConsent));
      } catch {
        setConsent(null);
      }
    }
    setLoaded(true);
  }, []);

  const updateConsent = (preferences) => {
    const newConsent = {
      essential: true, // Toujours true
      analytics: preferences.analytics || false,
      marketing: preferences.marketing || false,
      timestamp: new Date().toISOString()
    };
    
    setConsent(newConsent);
    localStorage.setItem(CONSENT_KEY, JSON.stringify(newConsent));
    localStorage.setItem(CONSENT_TIMESTAMP_KEY, newConsent.timestamp);
    
    // Sauvegarder en base
    saveConsentToServer(newConsent);
    
    // Charger/décharger les scripts selon le consentement
    handleScriptsConsent(newConsent);
  };

  const saveConsentToServer = async (preferences) => {
    try {
      await axios.post(`${API_URL}/api/consent/cookies`, {
        preferences: {
          essential: true,
          analytics: preferences.analytics,
          marketing: preferences.marketing
        }
      });
    } catch (error) {
      console.error('Failed to save consent to server:', error);
    }
  };

  const handleScriptsConsent = (preferences) => {
    // Google Analytics 4
    if (preferences.analytics) {
      loadGoogleAnalytics();
    } else {
      disableGoogleAnalytics();
    }

    // Meta Pixel
    if (preferences.marketing) {
      loadMetaPixel();
      loadTikTokPixel();
    } else {
      disableMetaPixel();
      disableTikTokPixel();
    }
  };

  const hasConsent = consent !== null;

  return (
    <CookieConsentContext.Provider value={{ 
      consent, 
      hasConsent, 
      loaded,
      updateConsent,
      hasAnalytics: consent?.analytics || false,
      hasMarketing: consent?.marketing || false
    }}>
      {children}
    </CookieConsentContext.Provider>
  );
}

// Fonctions de chargement des scripts (placeholders - à configurer avec les vraies IDs)
function loadGoogleAnalytics() {
  // Ne charger que si pas déjà chargé
  if (window.gtag) return;
  
  // Placeholder - remplacer GA_MEASUREMENT_ID par le vrai ID
  const GA_ID = 'G-XXXXXXXXXX';
  
  // Créer le script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;
  gtag('js', new Date());
  gtag('config', GA_ID);
  
  console.log('Google Analytics loaded');
}

function disableGoogleAnalytics() {
  // Désactiver GA en supprimant le cookie
  document.cookie = '_ga=; Max-Age=0; path=/; domain=.' + window.location.hostname;
  document.cookie = '_gid=; Max-Age=0; path=/; domain=.' + window.location.hostname;
  console.log('Google Analytics disabled');
}

function loadMetaPixel() {
  if (window.fbq) return;
  
  // Placeholder - remplacer par le vrai Pixel ID
  const PIXEL_ID = 'XXXXXXXXXXXXXXX';
  
  !function(f,b,e,v,n,t,s){
    if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)
  }(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
  
  window.fbq('init', PIXEL_ID);
  window.fbq('track', 'PageView');
  
  console.log('Meta Pixel loaded');
}

function disableMetaPixel() {
  // Désactiver Meta Pixel
  if (window.fbq) {
    window.fbq = null;
  }
  console.log('Meta Pixel disabled');
}

function loadTikTokPixel() {
  if (window.ttq) return;
  
  // Placeholder - remplacer par le vrai Pixel ID
  const TIKTOK_ID = 'XXXXXXXXXXXXXXX';
  
  !function (w, d, t) {
    w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var o=document.createElement("script");o.type="text/javascript",o.async=!0,o.src=i+"?sdkid="+e+"&lib="+t;var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};
    ttq.load(TIKTOK_ID);
    ttq.page();
  }(window, document, 'ttq');
  
  console.log('TikTok Pixel loaded');
}

function disableTikTokPixel() {
  if (window.ttq) {
    window.ttq = null;
  }
  console.log('TikTok Pixel disabled');
}

// Composant Bannière Cookies
export function CookieBanner() {
  const { t } = useTranslation();
  const { hasConsent, updateConsent, loaded } = useCookieConsent();
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true,
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    if (loaded && !hasConsent) {
      // Ne pas afficher sur les pages admin
      if (window.location.pathname.startsWith('/admin')) {
        return;
      }
      // Afficher la bannière avec un délai
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    }
  }, [loaded, hasConsent]);

  const handleAcceptAll = () => {
    updateConsent({ essential: true, analytics: true, marketing: true });
    setShowBanner(false);
  };

  const handleRejectAll = () => {
    updateConsent({ essential: true, analytics: false, marketing: false });
    setShowBanner(false);
  };

  const handleSavePreferences = () => {
    updateConsent(preferences);
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div 
      className="fixed inset-x-0 bottom-0 z-50 p-4"
      data-testid="cookie-banner"
    >
      <div className="max-w-4xl mx-auto bg-mutagim-dark border border-gray-700 rounded-xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-800">
          <div className="flex items-center gap-3">
            <Cookie className="h-6 w-6 text-mutagim-red" />
            <h2 className="text-lg font-semibold text-white">
              {t('cookies.banner.title')}
            </h2>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleRejectAll}
            className="text-gray-400 hover:text-white"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Description */}
        <div className="p-4">
          <p className="text-gray-300 text-sm mb-4">
            {t('cookies.banner.description')}
          </p>

          {/* Toggle détails */}
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="flex items-center gap-2 text-mutagim-red hover:underline text-sm mb-4"
            data-testid="cookie-customize-btn"
          >
            {showDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            {t('cookies.banner.customize')}
          </button>

          {/* Détails des catégories */}
          {showDetails && (
            <div className="space-y-4 mb-6 bg-gray-900/50 p-4 rounded-lg">
              {/* Essentiels */}
              <CookieCategory
                icon={<Shield className="h-5 w-5" />}
                name={t('cookies.categories.essential.name')}
                description={t('cookies.categories.essential.description')}
                checked={true}
                disabled={true}
                testId="cookie-essential"
              />
              
              {/* Analytics */}
              <CookieCategory
                icon={<BarChart3 className="h-5 w-5" />}
                name={t('cookies.categories.analytics.name')}
                description={t('cookies.categories.analytics.description')}
                checked={preferences.analytics}
                onChange={(checked) => setPreferences(p => ({ ...p, analytics: checked }))}
                testId="cookie-analytics"
              />
              
              {/* Marketing */}
              <CookieCategory
                icon={<Megaphone className="h-5 w-5" />}
                name={t('cookies.categories.marketing.name')}
                description={t('cookies.categories.marketing.description')}
                checked={preferences.marketing}
                onChange={(checked) => setPreferences(p => ({ ...p, marketing: checked }))}
                testId="cookie-marketing"
              />
            </div>
          )}

          {/* Boutons d'action */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={handleRejectAll}
              variant="outline"
              className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-800"
              data-testid="cookie-reject-all"
            >
              {t('cookies.banner.rejectAll')}
            </Button>
            
            {showDetails ? (
              <Button
                onClick={handleSavePreferences}
                className="flex-1 bg-mutagim-red hover:bg-red-700 text-white"
                data-testid="cookie-save-preferences"
              >
                {t('cookies.banner.savePreferences')}
              </Button>
            ) : (
              <Button
                onClick={handleAcceptAll}
                className="flex-1 bg-mutagim-red hover:bg-red-700 text-white"
                data-testid="cookie-accept-all"
              >
                {t('cookies.banner.acceptAll')}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Sous-composant pour chaque catégorie de cookie
function CookieCategory({ icon, name, description, checked, onChange, disabled = false, testId }) {
  return (
    <div className="flex items-start gap-4">
      <div className={cn(
        "p-2 rounded-lg",
        checked ? "bg-mutagim-red/20 text-mutagim-red" : "bg-gray-800 text-gray-400"
      )}>
        {icon}
      </div>
      <div className="flex-1">
        <div className="flex items-center justify-between">
          <h3 className="text-white font-medium">{name}</h3>
          <Switch
            checked={checked}
            onCheckedChange={onChange}
            disabled={disabled}
            className={cn(
              disabled && "opacity-50 cursor-not-allowed"
            )}
            data-testid={testId}
          />
        </div>
        <p className="text-gray-400 text-sm mt-1">{description}</p>
      </div>
    </div>
  );
}

export default CookieBanner;
